// lucide icons
import {
  LayoutDashboard,
  BookOpen,
  PieChart,
  BarChart3,
  ShoppingCart,
  Boxes,
  Users,
  User,
  Truck,
  Wallet,
  FileText,
  PackageOpen,
  Package,
  Tags,
  Building,
  Banknote,
  Recycle,
  Receipt,
  TrendingUp,
  TrendingDown,
  UtensilsCrossed,
  KeyRound,
  Factory,
  PackageCheck
} from 'lucide-react';

// icons
const icons = {
  LayoutDashboard,
  Boxes,
  Users,
  User,
  Truck,
  Wallet,
  FileText,
  PackageOpen,
  Package,
  ShoppingCart,
  BookOpen,
  PieChart,
  Tags,
  Building,
  Banknote,
  Recycle,
  Receipt,
  TrendingUp,
  TrendingDown,
  UtensilsCrossed,
  KeyRound,
  BarChart3,
  Factory,
  PackageCheck
};

// Storage keys
const MENU_STORAGE_KEY = 'dashboard_menu_data';
const MENU_BACKUP_KEY = 'dashboard_menu_backup';
const MENU_PROTECTION_KEY = 'menu_protection_active';

const isDistributorUser = () => {
  try {
    return sessionStorage.getItem('company_type') === 'distributor';
  } catch (error) {
    return false;
  }
};

const distributorMenu = [
  {
    id: 'group-distributor',
    title: 'Distributor',
    type: 'group',
    children: [
      {
        id: 'distributor-dashboard',
        title: 'Dashboard',
        type: 'item',
        url: '/distributor/dashboard',
        icon: icons.LayoutDashboard,
        breadcrumbs: false
      },
      {
        id: 'distributor-sales',
        title: 'Sales',
        type: 'collapse',
        icon: icons.ShoppingCart,
        children: [
          { id: 'distributor-invoices', title: 'Invoices', type: 'item', url: '/distributor/invoices', icon: icons.Receipt },
          { id: 'distributor-cheques', title: 'Cheques', type: 'item', url: '/distributor/cheques', icon: icons.Banknote },
          { id: 'distributor-credit-notes', title: 'Credit Notes', type: 'item', url: '/distributor/credit-notes', icon: icons.FileText },
          { id: 'distributor-debit-notes', title: 'Debit Notes', type: 'item', url: '/distributor/debit-notes', icon: icons.FileText },
          { id: 'distributor-price-lists', title: 'Price Lists', type: 'item', url: '/distributor/price-lists', icon: icons.Tags },
          { id: 'distributor-discount-rules', title: 'Discount Rules', type: 'item', url: '/distributor/discount-rules', icon: icons.TrendingUp }
        ]
      },
      {
        id: 'distributor-financials',
        title: 'Financials',
        type: 'collapse',
        icon: icons.PieChart,
        children: [
          { id: 'distributor-coa', title: 'Chart of Accounts', type: 'item', url: '/distributor/coa', icon: icons.BookOpen },
          { id: 'distributor-cost-centers', title: 'Cost Centers', type: 'item', url: '/distributor/cost-centers', icon: icons.Building }
        ]
      },
      {
        id: 'distributor-reports',
        title: 'Reports',
        type: 'collapse',
        icon: icons.BarChart3,
        children: [
          { id: 'distributor-report-sales', title: 'Sales', type: 'item', url: '/distributor/reports/sales', icon: icons.TrendingUp },
          { id: 'distributor-report-purchases', title: 'Purchases', type: 'item', url: '/distributor/reports/purchases', icon: icons.TrendingDown },
          { id: 'distributor-report-aging', title: 'Aging', type: 'item', url: '/distributor/reports/aging', icon: icons.Receipt },
          { id: 'distributor-report-trial-balance', title: 'Trial Balance', type: 'item', url: '/distributor/reports/trial-balance', icon: icons.PieChart },
          { id: 'distributor-report-balance-sheet', title: 'Balance Sheet', type: 'item', url: '/distributor/reports/balance-sheet', icon: icons.BookOpen },
          { id: 'distributor-report-income-statement', title: 'Income Statement', type: 'item', url: '/distributor/reports/income-statement', icon: icons.FileText },
          { id: 'distributor-report-expenses', title: 'Expenses', type: 'item', url: '/distributor/reports/expenses', icon: icons.Wallet },
          { id: 'distributor-report-stock-adjustments', title: 'Stock Adjustments', type: 'item', url: '/distributor/reports/stock-adjustments', icon: icons.Boxes },
          { id: 'distributor-report-cash-flow', title: 'Cash Flow', type: 'item', url: '/distributor/reports/cash-flow', icon: icons.Banknote }
        ]
      },
      {
        id: 'distributor-company-setup',
        title: 'Company Setup',
        type: 'collapse',
        icon: icons.Building,
        children: [
          { id: 'distributor-companies', title: 'Companies', type: 'item', url: '/company', icon: icons.Building },
          { id: 'distributor-departments', title: 'Departments', type: 'item', url: '/distributor/departments', icon: icons.Users },
          { id: 'distributor-user-management', title: 'User Management', type: 'item', url: '/users', icon: icons.User },
          { id: 'distributor-approvals', title: 'Approvals', type: 'item', url: '/company', icon: icons.FileText, disabled: true }
        ]
      }
    ]
  }
];

const defaultDashboard = [
  {
    id: 'group-dashboard',
    title: 'Main Menu',
    type: 'group',
    children: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'item',
        url: '/dashboard/default',
        icon: icons.LayoutDashboard,
        breadcrumbs: false
      }
    ]
  }
];

// Helper function to map icon strings to icon components recursively
const mapIconsInMenu = (menuData) => {
  if (!menuData) return null;

  const mapItem = (item) => {
    const mappedItem = { ...item };

    // Map icon string to icon component
    if (typeof item.icon === 'string') {
      mappedItem.icon = icons[item.icon] || icons.Package;
    }

    // Recursively map children
    if (item.children && Array.isArray(item.children)) {
      mappedItem.children = item.children.map(mapItem);
    }

    return mappedItem;
  };

  // menuData is an array of root-level groups — map icons in each one.
  return Array.isArray(menuData) ? menuData.map(mapItem) : mapItem(menuData);
};

// Function to get menu from sessionStorage with protection
const getMenuFromStorage = () => {
  try {
    if (isDistributorUser()) {
      return mapIconsInMenu(distributorMenu);
    }

    // Try to get menu from sessionStorage
    const storedMenu = sessionStorage.getItem(MENU_STORAGE_KEY);

    if (storedMenu) {
      const parsedMenu = JSON.parse(storedMenu);
      const mappedMenu = mapIconsInMenu(parsedMenu);

      // Save backup for protection
      sessionStorage.setItem(MENU_BACKUP_KEY, storedMenu);
      sessionStorage.setItem(MENU_PROTECTION_KEY, 'true');

      return mappedMenu;
    }

    // Check if backup exists (browser console removal protection)
    const backupMenu = sessionStorage.getItem(MENU_BACKUP_KEY);
    const isProtected = sessionStorage.getItem(MENU_PROTECTION_KEY);

    if (backupMenu && isProtected === 'true') {
      // Restore from backup
      const parsedMenu = JSON.parse(backupMenu);
      const mappedMenu = mapIconsInMenu(parsedMenu);

      // Restore main storage
      sessionStorage.setItem(MENU_STORAGE_KEY, backupMenu);

      return mappedMenu;
    }

    // No storage data, return default
    return defaultDashboard;
  } catch (error) {
    console.error('Error loading menu from storage:', error);
    return defaultDashboard;
  }
};

// Function to programmatically remove menu (code-based removal)
export const removeMenuData = () => {
  try {
    sessionStorage.removeItem(MENU_STORAGE_KEY);
    sessionStorage.removeItem(MENU_BACKUP_KEY);
    sessionStorage.removeItem(MENU_PROTECTION_KEY);
    console.log('Menu data removed permanently');
  } catch (error) {
    console.error('Error removing menu:', error);
  }
};

// Function to update menu programmatically
export const updateMenuData = (newMenuData) => {
  try {
    const menuString = JSON.stringify(newMenuData);
    sessionStorage.setItem(MENU_STORAGE_KEY, menuString);
    sessionStorage.setItem(MENU_BACKUP_KEY, menuString);
    sessionStorage.setItem(MENU_PROTECTION_KEY, 'true');
    console.log('Menu data updated successfully');

    // Force reload to reflect changes
    window.location.reload();
  } catch (error) {
    console.error('Error updating menu:', error);
  }
};

// ==============================|| MENU ITEMS - DASHBOARD ||============================== //

// Initialize menu - yeh har load pe call hoga
let dashboard = getMenuFromStorage();

// Make sure dashboard always has proper structure — now an ARRAY of
// root-level groups, so validate it's a non-empty array instead of
// checking a single object's `.id`.
if (!Array.isArray(dashboard) || dashboard.length === 0) {
  dashboard = defaultDashboard;
}

export default dashboard;